import { COLORS_LARGE_SET } from "@tokenterminal/core/feature/chart/tt-ui-chart/colors"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"

import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import Link from "next/link"
import { useState } from "react"
import { type MetricConfiguration } from "utils/trpc"

const chartColors = Object.values(COLORS_LARGE_SET)

const MAX_ITEMS = 10

export const SidebarLegend = ({
  items,
  metric,
  interval,
  metricTitle,
}: {
  items: {
    name: string
    href: string
    value: number
    logo?: string
  }[]
  metric: MetricConfiguration | undefined
  interval: Interval
  metricTitle: string
}) => {
  const [isProjectsLegendModalOpen, setIsProjectsLegendModalOpen] =
    useState(false)
  if (!metric) return null

  const intervalName =
    interval === "max" ? "All time" : interval === "ytd" ? "YTD" : interval

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between mb-2 text-fg-secondary text-sm">
        <div>Projects</div>
        <div>{metric.static ? "Latest" : `${intervalName} sum`}</div>
      </div>
      <div className="flex flex-col gap-3">
        {items.slice(0, MAX_ITEMS).map((item, index) => {
          const formattedValue = formatNumber({
            value: item.value,
            format: "compact",
            metricFormat: metric.format,
          })

          return (
            <div
              key={item.name}
              className="flex items-center justify-between w-full rounded-lg gap-6"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="w-2 h-2 rounded-sm"
                  style={{
                    backgroundColor: chartColors[index % chartColors.length],
                  }}
                />
                <Tooltip
                  content={
                    <div className="flex items-start gap-2 p-1">
                      {item.logo && (
                        <Avatar
                          src={`https://static1.tokenterminal.com/${item.logo}`}
                          className="w-3 h-3 rounded-full overflow-hidden flex-shrink-0 mt-1"
                        />
                      )}
                      <div className="flex flex-col gap-0.5">
                        <div className="text-sm">{item.name}</div>
                        <div className="text-xs text-fg-secondary">
                          Click to view project
                        </div>
                      </div>
                    </div>
                  }
                  tooltipProps={{ placement: "left", className: "rounded-lg" }}
                  delay={300}
                >
                  <Tooltip.Trigger>
                    <Link
                      href={item.href}
                      className="truncate text-sm hover:underline max-w-[200px]"
                      style={{
                        textDecorationColor: "hsl(var(--neutral-500) / 0.75)",
                        textUnderlineOffset: "3px",
                        textDecorationStyle: "dotted",
                      }}
                    >
                      {item.name}
                    </Link>
                  </Tooltip.Trigger>
                </Tooltip>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-sm text-fg-secondary whitespace-nowrap">
                  {formattedValue}
                </span>
              </div>
            </div>
          )
        })}
        {items.length > MAX_ITEMS && (
          <a
            onClick={() => setIsProjectsLegendModalOpen(true)}
            className="text-sm text-fg-secondary hover:underline cursor-pointer"
          >
            View {items.length - MAX_ITEMS} more
          </a>
        )}
      </div>
      <Modal
        isOpen={isProjectsLegendModalOpen}
        onOpenChange={setIsProjectsLegendModalOpen}
      >
        <Modal.Header title={metricTitle} subtitle={metric.title} />
        <Modal.Body>
          <div>
            <div className="flex justify-between mb-2 px-0 text-fg-secondary text-sm">
              <div>Projects</div>
              <div>
                {metric.static ? "Latest value" : "Sum"} for{" "}
                {interval === "max" ? "all time" : interval}
              </div>
            </div>
            <div className="flex flex-col border border-solid border-border-default rounded-lg overflow-hidden">
              {items.map(({ name, href, value }, index) => (
                <Link
                  href={href}
                  key={name}
                  className="flex justify-between items-center gap-2 px-3 py-2 hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 border-b border-solid border-border-default last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{
                        background: chartColors[index % chartColors.length],
                      }}
                    />
                    <span>{name}</span>
                  </div>
                  <div>
                    {formatNumber({
                      value,
                      format: "compact",
                      metricFormat: metric.format,
                    })}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-end">
          <Button onPress={() => setIsProjectsLegendModalOpen(false)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
